import { ReactNode, useMemo } from "react";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "@digital-twins/design-system/icons";
import {
  halfWindowDelta,
  halfWindowSplit,
  SocialTopicDelta,
  SocialTopicMetric,
} from "@digital-twins/shared-types";
import { intlLocaleForUi } from "@/i18n";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { topicStats } from "./charts/socialTopicMetrics";
import { SocialTopicSlotContext } from "./SocialTopicView";
import { useSocialUniverseCounts } from "./useSocialUniverse";
import { SocialSentimentBar } from "./SocialSentimentBar";
import {
  formatTopicStat,
  SocialMeasureSwitch,
  SocialSparkline,
  SocialStat,
  SocialStatDelta,
} from "./SocialStatRow";
import { useSocialTopicSeries, useSocialTopicSeriesQuery } from "./useSocialTopicSeries";

/**
 * Frozen for the stat row (plan D8): Reach with its coverage note, Posts and
 * Creators with deltas, Engagement, the Reach / Posts switch (the measure
 * lives on `context.topicFilters`) and the overview toggle.
 */
interface SocialTopicKpisProps {
  context: SocialTopicSlotContext;
  /** The overview region the toggle controls (`aria-controls`); absent hides the toggle. */
  overviewId?: string;
  isOverviewOpen?: boolean;
  onToggleOverview?: () => void;
}

/** Moved to `SocialStatRow`; re-exported for the Topic panels that import it from here. */
export { formatTopicStat };

/** "Oct 7" for a `YYYY-MM-DD` UTC day. */
function formatDay(day: string, uiLanguage: string): string {
  const date = new Date(`${day}T00:00:00.000Z`);
  if (Number.isNaN(date.getTime())) {
    return day;
  }
  return new Intl.DateTimeFormat(intlLocaleForUi(uiLanguage), {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }).format(date);
}

/** The delta's aria label: "Up {value} since {date}" or "Down {value} since {date}". */
function deltaAriaLabel(
  delta: SocialTopicDelta | null,
  since: string,
  t: TFunction<"app">,
  uiLanguage: string,
): string {
  if (!delta) {
    return "";
  }
  const magnitude = formatTopicStat(Math.abs(delta.change), uiLanguage);
  return delta.direction === "down"
    ? t("socialListening.topicPage.stats.deltaDown", { value: magnitude, date: since })
    : t("socialListening.topicPage.stats.deltaUp", { value: magnitude, date: since });
}

/**
 * The Topic page's stat row (concept §5.3, plan D3). Reach (views) with its
 * coverage note and no delta, since views are a snapshot at collection;
 * Posts and Creators with the change between the two halves of the window
 * by publish date; Engagement as likes, comments and shares. Every figure is
 * under every filter. Deltas hide while a date range is set. On the right:
 * the change caption, the Reach / Posts switch and the overview toggle.
 */
export function SocialTopicKpis(props: SocialTopicKpisProps) {
  const { context, overviewId, isOverviewOpen = true, onToggleOverview } = props;
  const { universe, topicFilters, refreshKey } = context;

  const { t, i18n } = useTranslation("app");
  const token = universe.token;
  const { filters } = topicFilters;
  const metric = filters.metric;
  const hasWindow = filters.window != null;
  const allFilters = useMemo(() => topicFilters.facetFilters(), [topicFilters]);

  // The totals under every filter (any groupBy carries them; channel is the one
  // the filter bar's count reads too).
  const { counts: totals } = useSocialUniverseCounts(
    token,
    { groupBy: "channel", filters: allFilters, includeMarket: true },
    refreshKey,
  );
  const seriesQuery = useSocialTopicSeriesQuery(context);
  const { series, postBuckets } = useSocialTopicSeries(token, seriesQuery, refreshKey);
  const split = useMemo(
    () => (series && !hasWindow ? halfWindowSplit(postBuckets, series.bucket) : null),
    [series, postBuckets, hasWindow],
  );
  const { counts: previousHalf } = useSocialUniverseCounts(
    split ? token : null,
    { groupBy: "channel", filters: { ...allFilters, window: split?.previous } },
    refreshKey,
  );
  const { counts: currentHalf } = useSocialUniverseCounts(
    split ? token : null,
    { groupBy: "channel", filters: { ...allFilters, window: split?.current } },
    refreshKey,
  );

  const stats = useMemo(() => topicStats(totals), [totals]);
  const postsDelta = split
    ? halfWindowDelta({
        current: split.currentCounts.posts,
        previous: split.previousCounts.posts,
        hasWindow,
      })
    : null;
  const creatorsDelta =
    split && previousHalf && currentHalf
      ? halfWindowDelta({
          current: topicStats(currentHalf)?.creators ?? null,
          previous: topicStats(previousHalf)?.creators ?? null,
          hasWindow,
        })
      : null;
  const since = split ? formatDay(split.mid, i18n.language) : "";

  const setMetric = (next: SocialTopicMetric) => {
    topicFilters.setFilter("metric", next);
  };

  if (!stats) {
    return (
      <div
        className="flex flex-wrap items-center gap-8 py-4 animate-in fade-in fill-mode-both delay-150"
        data-testid="social-topic-kpis-loading"
        aria-hidden="true"
      >
        {[0, 1, 2, 3].map((index) => (
          <Skeleton
            key={index}
            className="h-14 w-28 rounded-lg border border-card-border bg-background"
          />
        ))}
      </div>
    );
  }

  // Views on every post is the norm: the note only speaks when coverage is partial.
  const reachNote =
    stats.viewsPosts == null || stats.viewsPosts >= stats.posts
      ? null
      : t("socialListening.topicPage.stats.reachCoverage", {
          covered: formatTopicStat(stats.viewsPosts, i18n.language),
          count: stats.posts,
        });
  const format = (value: number) => formatTopicStat(value, i18n.language);
  // Engagement rate (engagement over reach) says more than the raw sum's definition.
  const engagementNote =
    stats.engagement != null && stats.reach != null && stats.reach > 0
      ? t("socialListening.topicPage.stats.engagementRate", {
          value: new Intl.NumberFormat(intlLocaleForUi(i18n.language), {
            style: "percent",
            maximumFractionDigits: 1,
          }).format(stats.engagement / stats.reach),
        })
      : t("socialListening.topicPage.stats.engagementNote");

  const toggle =
    overviewId && onToggleOverview ? (
      <button
        type="button"
        aria-controls={overviewId}
        aria-expanded={isOverviewOpen}
        className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-full border border-border px-3 text-[13px] font-medium text-foreground transition-colors hover:bg-accent"
        data-testid="social-topic-overview-toggle"
        onClick={onToggleOverview}
      >
        <ChevronDown
          className={cn(
            "h-4 w-4 transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none",
            isOverviewOpen && "rotate-180",
          )}
          aria-hidden="true"
        />
        {isOverviewOpen
          ? t("socialListening.topicPage.stats.hideOverview")
          : t("socialListening.topicPage.stats.showOverview")}
      </button>
    ) : null;

  const postsDeltaNode = (
    <SocialStatDelta
      id="posts"
      delta={postsDelta}
      label={deltaAriaLabel(postsDelta, since, t, i18n.language)}
    />
  );
  const creatorsDeltaNode = (
    <SocialStatDelta
      id="creators"
      delta={creatorsDelta}
      label={deltaAriaLabel(creatorsDelta, since, t, i18n.language)}
    />
  );

  const isCollapsed = Boolean(overviewId && onToggleOverview && !isOverviewOpen);
  const summaryParts: Array<{ id: string; value: number | null; label: string; delta?: ReactNode }> = [
    { id: "reach", value: stats.reach, label: t("socialListening.topicPage.stats.summary.reach") },
    {
      id: "posts",
      value: stats.posts,
      label: t("socialListening.topicPage.stats.summary.posts", { count: stats.posts }),
      delta: postsDeltaNode,
    },
    {
      id: "creators",
      value: stats.creators,
      label: t("socialListening.topicPage.stats.summary.creators", {
        count: stats.creators ?? 0,
      }),
      delta: creatorsDeltaNode,
    },
    {
      id: "engagement",
      value: stats.engagement,
      label: t("socialListening.topicPage.stats.summary.engagement"),
    },
  ];
  const reachSpark = postBuckets.map((entry) => entry.total.views);
  const postsSpark = postBuckets.map((entry) => entry.total.posts);

  // Collapsed, the top row is the whole band: one line of the same figures, so
  // the feed gets the first screen. The toggle keeps its place in both states,
  // so it keeps focus across a toggle.
  const lead = isCollapsed ? (
    <div
      className="flex min-w-0 flex-wrap items-baseline gap-x-5 gap-y-1 text-[13px] text-muted-foreground"
      data-testid="social-topic-kpis-summary"
    >
      {summaryParts
        .filter((part) => part.value != null)
        .map((part) => (
          <span
            key={part.id}
            className="inline-flex items-baseline gap-1.5 whitespace-nowrap"
            data-testid={`social-topic-kpis-summary-${part.id}`}
          >
            <b className="text-[15px] font-semibold tabular-nums text-foreground">
              {format(part.value as number)}
            </b>
            {part.label}
            {part.delta}
          </span>
        ))}
    </div>
  ) : (
    <span />
  );

  return (
    <section
      aria-label={t("socialListening.topicPage.stats.ariaLabel")}
      className={cn(
        "mt-4 rounded-2xl border border-card-border bg-background px-5",
        isCollapsed ? "py-2.5" : "mb-4 pb-3 pt-2.5",
      )}
      data-state={isCollapsed ? "collapsed" : "expanded"}
      data-testid="social-topic-kpis"
    >
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
        {lead}
        <div className="flex items-center gap-2">
          {isCollapsed ? null : (
            <SocialMeasureSwitch
              options={(["reach", "posts"] as Array<SocialTopicMetric>).map((option) => ({
                value: option,
                label: t(`socialListening.topicPage.stats.metric.${option}`),
              }))}
              value={metric}
              onChange={setMetric}
              ariaLabel={t("socialListening.topicPage.stats.measureAria")}
              testIdPrefix="social-topic-metric"
            />
          )}
          {toggle}
        </div>
      </div>
      {isCollapsed ? null : (
        <div className="mt-2.5 grid grid-cols-2 gap-x-6 gap-y-3 border-t border-border/60 pt-3 @[760px]/topic:grid-flow-col @[760px]/topic:auto-cols-fr @[760px]/topic:grid-cols-none">
          <SocialStat
            id="reach"
            label={t("socialListening.topicPage.stats.reach")}
            value={stats.reach}
            note={reachNote}
            spark={<SocialSparkline values={reachSpark} testId="social-topic-kpi-reach-spark" />}
          />
          <SocialStat
            id="posts"
            label={t("socialListening.topicPage.stats.posts")}
            value={stats.posts}
            delta={postsDeltaNode}
            spark={<SocialSparkline values={postsSpark} testId="social-topic-kpi-posts-spark" />}
          />
          <SocialStat
            id="creators"
            label={t("socialListening.topicPage.stats.creators")}
            value={stats.creators}
            delta={creatorsDeltaNode}
          />
          <SocialStat
            id="engagement"
            label={t("socialListening.topicPage.stats.engagement")}
            value={stats.engagement}
            note={engagementNote}
          />
          <SocialSentimentBar context={context} />
        </div>
      )}
    </section>
  );
}
